import QRCode from "qrcode";
import { jsPDF } from "jspdf";
import type { Part } from "@/hooks/useParts";

export type LabelSize = "standard" | "small";

export interface PartsPurchaser {
  name: string | null;
  email: string | null;
}

export interface PartLabelData {
  part: Part;
  locations: { facility: string; zone: string; quantity: number; unit: string }[];
  purchaser: PartsPurchaser;
  orgName: string | null;
  orgLogoUrl: string | null;
  partImageUrl?: string | null;
  reorderQuantity?: number | null;
}

/**
 * Builds the short mailto: URL encoded in the QR code.
 * Body is intentionally omitted — shorter URL = simpler QR = scannable when printed.
 */
export function buildMailtoUrl(data: PartLabelData): string {
  const { part, purchaser } = data;
  const to = purchaser.email ?? "";
  const subject = encodeURIComponent(
    `Reorder Request: ${part.name}${part.part_number ? ` (${part.part_number})` : ""}`
  );
  return `mailto:${to}?subject=${subject}`;
}

/**
 * Generates a data-URL PNG of the QR code.
 * Uses error correction "M" (no logo overlay) — keeps the QR grid small enough
 * to scan reliably when printed on a thermal label at ≤1" size.
 */
export async function generateQrDataUrl(mailto: string): Promise<string> {
  return QRCode.toDataURL(mailto, {
    width: 300,
    margin: 2,
    errorCorrectionLevel: "M",
    color: { dark: "#000000", light: "#ffffff" },
  });
}

/** Loads an image URL into an HTMLImageElement, resolves when done (or on error). */
function loadImage(src: string, crossOrigin = false): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/**
 * Renders the small 3.5" × 1" label to a canvas and returns a PNG data URL.
 * Layout: [info | photo | QR] all in a single horizontal strip.
 */
async function renderSmallLabelCanvas(data: PartLabelData, qrDataUrl: string): Promise<string> {
  const S = 2;
  const W = Math.round(3.5 * 96 * S);  // 672
  const H = Math.round(1 * 96 * S);    // 192

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);

  const PAD = 8 * S;
  const GAP = 5 * S;
  const imgSize = H - PAD * 2;        // ~176px — photo & QR are full column height
  const qrX = W - PAD - imgSize;
  const photoX = qrX - GAP - imgSize;
  const infoW = photoX - PAD - GAP;

  // ── Photo column ─────────────────────────────────────────────────────────────
  if (data.partImageUrl) {
    const img = await loadImage(data.partImageUrl, true);
    if (img) {
      const r = 3 * S;
      const px = photoX, py = PAD, ps = imgSize;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(px + r, py);
      ctx.arcTo(px + ps, py, px + ps, py + ps, r);
      ctx.arcTo(px + ps, py + ps, px, py + ps, r);
      ctx.arcTo(px, py + ps, px, py, r);
      ctx.arcTo(px, py, px + ps, py, r);
      ctx.closePath();
      ctx.clip();
      ctx.filter = "grayscale(1) contrast(1.1)";
      ctx.drawImage(img, px, py, ps, ps);
      ctx.filter = "none";
      ctx.restore();
    }
  } else {
    // grey placeholder so layout doesn't shift when there's no photo
    ctx.fillStyle = "#eeeeee";
    ctx.fillRect(photoX, PAD, imgSize, imgSize);
  }

  // ── QR column ────────────────────────────────────────────────────────────────
  const qrImg = await loadImage(qrDataUrl);
  if (qrImg) ctx.drawImage(qrImg, qrX, PAD, imgSize, imgSize);

  // ── Info column ──────────────────────────────────────────────────────────────
  let iy = PAD + 2 * S;

  // Part name (bold, up to 2 lines)
  ctx.fillStyle = "#111111";
  ctx.font = `bold ${11 * S}px -apple-system, sans-serif`;
  const nameWords = data.part.name.split(" ");
  let nameLine = "";
  const nameLines: string[] = [];
  for (const word of nameWords) {
    const test = nameLine ? `${nameLine} ${word}` : word;
    if (ctx.measureText(test).width > infoW && nameLine) {
      nameLines.push(nameLine);
      nameLine = word;
    } else {
      nameLine = test;
    }
  }
  if (nameLine) nameLines.push(nameLine);
  for (const line of nameLines.slice(0, 2)) {
    ctx.fillText(line, PAD, iy + 11 * S, infoW);
    iy += 13 * S;
  }

  // Part number
  if (data.part.part_number) {
    ctx.fillStyle = "#666666";
    ctx.font = `${8 * S}px monospace`;
    ctx.fillText(`# ${data.part.part_number}`, PAD, iy + 8 * S, infoW);
    iy += 11 * S;
  }

  // Divider
  iy += 3 * S;
  ctx.strokeStyle = "#dddddd";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(PAD, iy);
  ctx.lineTo(PAD + infoW, iy);
  ctx.stroke();
  iy += 5 * S;

  // Min qty + Reorder qty side by side
  ctx.fillStyle = "#999999";
  ctx.font = `600 ${7 * S}px -apple-system, sans-serif`;
  ctx.fillText("MIN QTY", PAD, iy + 7 * S);
  const reorderLabelX = PAD + infoW * 0.52;
  if (data.reorderQuantity != null) ctx.fillText("REORDER QTY", reorderLabelX, iy + 7 * S);
  iy += 10 * S;

  ctx.fillStyle = "#333333";
  ctx.font = `${9 * S}px -apple-system, sans-serif`;
  ctx.fillText(`${data.part.min_quantity ?? "—"} ${data.part.unit ?? ""}`.trim(), PAD, iy + 9 * S);
  if (data.reorderQuantity != null) {
    ctx.fillText(`${data.reorderQuantity} ${data.part.unit ?? ""}`.trim(), reorderLabelX, iy + 9 * S);
  }

  return canvas.toDataURL("image/png");
}

/**
 * Renders the label to a canvas and returns a PNG data URL.
 * 2× retina scale so text and images are sharp on the Dymo.
 * Label: 4in × 2.3125in @ 96dpi = 384 × 222 px → 768 × 444 at 2×.
 */
async function renderLabelCanvas(data: PartLabelData, qrDataUrl: string): Promise<string> {
  const S = 2; // scale factor
  const W = Math.round(4 * 96 * S);       // 768
  const H = Math.round(2.3125 * 96 * S);  // 444

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;

  // ── Background ──────────────────────────────────────────────────────────────
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);

  // ── Layout constants (all in canvas px) ────────────────────────────────────
  const PAD_L = 12 * S;
  const PAD_T = 10 * S;
  const PAD_R = 10 * S;
  const PAD_B = 8 * S;
  const COL_GAP = 6 * S;

  const PHOTO_SIZE = 70 * S;
  const QR_SIZE = 80 * S;
  const QR_HINT_H = 20 * S;
  const RIGHT_W = QR_SIZE;

  const infoX = PAD_L;
  const infoW = W - PAD_L - COL_GAP - RIGHT_W - PAD_R;
  const qrX = PAD_L + infoW + COL_GAP;

  // ── QR column ───────────────────────────────────────────────────────────────
  // Centre the stack (photo? + QR + hint) vertically
  const stackH = (data.partImageUrl ? PHOTO_SIZE + 2 * S : 0) + QR_SIZE + 2 * S + QR_HINT_H;
  let qrY = PAD_T + Math.max(0, (H - PAD_T - PAD_B - stackH) / 2);

  if (data.partImageUrl) {
    const img = await loadImage(data.partImageUrl, true);
    if (img) {
      const r = 4 * S;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(qrX + r, qrY);
      ctx.arcTo(qrX + PHOTO_SIZE, qrY, qrX + PHOTO_SIZE, qrY + PHOTO_SIZE, r);
      ctx.arcTo(qrX + PHOTO_SIZE, qrY + PHOTO_SIZE, qrX, qrY + PHOTO_SIZE, r);
      ctx.arcTo(qrX, qrY + PHOTO_SIZE, qrX, qrY, r);
      ctx.arcTo(qrX, qrY, qrX + PHOTO_SIZE, qrY, r);
      ctx.closePath();
      ctx.clip();
      ctx.filter = "grayscale(1) contrast(1.1)";
      ctx.drawImage(img, qrX, qrY, PHOTO_SIZE, PHOTO_SIZE);
      ctx.filter = "none";
      ctx.restore();
    }
    qrY += PHOTO_SIZE + 2 * S;
  }

  const qrImg = await loadImage(qrDataUrl);
  if (qrImg) ctx.drawImage(qrImg, qrX, qrY, QR_SIZE, QR_SIZE);
  qrY += QR_SIZE + 2 * S;

  ctx.fillStyle = "#aaaaaa";
  ctx.font = `${7 * S}px -apple-system, sans-serif`;
  ctx.textAlign = "center";
  ctx.fillText("Scan to reorder", qrX + QR_SIZE / 2, qrY + 7 * S);
  ctx.textAlign = "left";

  // ── Info column ─────────────────────────────────────────────────────────────
  let iy = PAD_T;

  // Part name
  ctx.fillStyle = "#111111";
  ctx.font = `bold ${15 * S}px -apple-system, sans-serif`;
  // Wrap long names
  const nameWords = data.part.name.split(" ");
  let nameLine = "";
  const nameLines: string[] = [];
  for (const word of nameWords) {
    const test = nameLine ? `${nameLine} ${word}` : word;
    if (ctx.measureText(test).width > infoW && nameLine) {
      nameLines.push(nameLine);
      nameLine = word;
    } else {
      nameLine = test;
    }
  }
  if (nameLine) nameLines.push(nameLine);
  for (const line of nameLines.slice(0, 2)) {
    ctx.fillText(line, infoX, iy + 15 * S);
    iy += 17 * S;
  }

  // Part number
  if (data.part.part_number) {
    ctx.fillStyle = "#555555";
    ctx.font = `${10 * S}px monospace`;
    ctx.fillText(`# ${data.part.part_number}`, infoX, iy + 10 * S);
    iy += 13 * S;
  }

  // Divider
  iy += 5 * S;
  ctx.strokeStyle = "#dddddd";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(infoX, iy);
  ctx.lineTo(infoX + infoW, iy);
  ctx.stroke();
  iy += 6 * S;

  // Location
  ctx.fillStyle = "#999999";
  ctx.font = `600 ${8 * S}px -apple-system, sans-serif`;
  ctx.fillText("LOCATION", infoX, iy + 8 * S);
  iy += 11 * S;

  ctx.fillStyle = "#333333";
  ctx.font = `${9 * S}px -apple-system, sans-serif`;
  const locLines = data.locations.length > 0
    ? data.locations.map((l) => `${l.facility} › ${l.zone} — ${l.quantity} ${l.unit}`)
    : ["—"];
  for (const line of locLines.slice(0, 2)) {
    ctx.fillText(line, infoX, iy + 9 * S, infoW);
    iy += 13 * S;
  }

  // Min qty + Reorder qty side by side
  iy += 2 * S;
  const halfW = infoW / 2 - 4 * S;
  ctx.fillStyle = "#999999";
  ctx.font = `600 ${8 * S}px -apple-system, sans-serif`;
  ctx.fillText("MIN QTY", infoX, iy + 8 * S);
  if (data.reorderQuantity != null) ctx.fillText("REORDER QTY", infoX + halfW + 8 * S, iy + 8 * S);
  iy += 11 * S;

  ctx.fillStyle = "#333333";
  ctx.font = `${9 * S}px -apple-system, sans-serif`;
  ctx.fillText(`${data.part.min_quantity ?? "—"} ${data.part.unit ?? ""}`.trim(), infoX, iy + 9 * S);
  if (data.reorderQuantity != null) {
    ctx.fillText(`${data.reorderQuantity} ${data.part.unit ?? ""}`.trim(), infoX + halfW + 8 * S, iy + 9 * S);
  }

  // ── Footer ──────────────────────────────────────────────────────────────────
  const footerY = H - PAD_B - 14 * S;

  ctx.strokeStyle = "#eeeeee";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(infoX, footerY - 3 * S);
  ctx.lineTo(infoX + infoW, footerY - 3 * S);
  ctx.stroke();

  ctx.fillStyle = "#bbbbbb";
  ctx.font = `${7 * S}px -apple-system, sans-serif`;
  ctx.textAlign = "right";
  ctx.fillText("Scan to reorder", infoX + infoW, footerY + 7 * S);
  ctx.textAlign = "left";

  if (data.orgLogoUrl) {
    const logo = await loadImage(data.orgLogoUrl, true);
    if (logo) {
      const maxH = 14 * S;
      const maxW = 60 * S;
      const ratio = Math.min(maxW / logo.width, maxH / logo.height);
      ctx.filter = "grayscale(1)";
      ctx.drawImage(logo, infoX, footerY, logo.width * ratio, logo.height * ratio);
      ctx.filter = "none";
    }
  } else if (data.orgName) {
    ctx.fillStyle = "#555555";
    ctx.font = `700 ${10 * S}px -apple-system, sans-serif`;
    ctx.fillText(data.orgName, infoX, footerY + 10 * S);
  }

  return canvas.toDataURL("image/png");
}

/**
 * Renders the label to a PDF sized exactly to the label dimensions and downloads it.
 * Open the downloaded file in Preview, then File → Print → select Dymo.
 * Preview respects the embedded page size so no paper-size fiddling is needed.
 */
export async function printPartLabel(data: PartLabelData, size: LabelSize = "standard"): Promise<void> {
  const isSmall = size === "small";

  const mailto = buildMailtoUrl(data);
  const qrDataUrl = await generateQrDataUrl(mailto);

  const labelPng = isSmall
    ? await renderSmallLabelCanvas(data, qrDataUrl)
    : await renderLabelCanvas(data, qrDataUrl);

  // PDF dimensions in inches
  const wIn = isSmall ? 3.5 : 4;
  const hIn = isSmall ? 1 : 2.3125;

  // jsPDF uses points (72pt = 1in) internally
  const pdf = new jsPDF({
    orientation: isSmall ? "landscape" : "portrait",
    unit: "in",
    format: [wIn, hIn],
  });

  pdf.addImage(labelPng, "PNG", 0, 0, wIn, hIn);

  const safeName = data.part.name.replace(/[^a-zA-Z0-9_-]/g, "_");
  pdf.save(`label_${safeName}.pdf`);
}
